const { sortBy, last, pick, pickBy, isEqual, } = require('lodash');
const { format: formatDate, parse: parseDate, addMonths, } = require('date-fns');

const valueFieldNames = ['ownerType', 'ownerId', 'type', 'description', 'amount'];

const scopes = {
  single: {
    label: 'この月のみ',
  },
  following: {
    label: 'この月以降',
  },
  all: {
    label: 'すべての月',
  },
};

const shiftMonth = (month, diff) => formatDate(addMonths(parseDate(month, 'yyyy-MM', new Date()), diff), 'yyyy-MM');

const sortedRevisions = _ => sortBy(_.revisions || [], 'startMonth');

const revisionAt = (recurringExpense, month) => last(sortedRevisions(recurringExpense).filter(_ => _.startMonth <= month));

const isActiveAt = (recurringExpense, month) => month >= recurringExpense.startMonth && (recurringExpense.endMonth == null || month <= recurringExpense.endMonth);

const resolve = (recurringExpense, month) => {
  if (!isActiveAt(recurringExpense, month)) return null;

  const override = recurringExpense.overrides?.[month];
  if (override?.deleted) return null;

  const revision = revisionAt(recurringExpense, month);
  if (revision == null) return null;

  return {
    ...pick(revision, valueFieldNames),
    ...pick(override, valueFieldNames),
    id: `${recurringExpense.id}__${month}`,
    recurringExpenseId: recurringExpense.id,
    behavior: 'fixed',
    month,
    isRecurring: true,
    isOverridden: override != null,
  };
};

const resolveAll = (recurringExpenses, month) => recurringExpenses.map(_ => resolve(_, month)).filter(Boolean);

const newRecurringExpense = ({ values, month, }) => ({
  startMonth: month,
  endMonth: null,
  revisions: [{ startMonth: month, ...pick(values, valueFieldNames) }],
  overrides: {},
});

const applyEdit = (recurringExpense, { scope, month, values, }) => {
  const after = pick(values, valueFieldNames);
  return ({
    single: () => ({
      overrides: { ...recurringExpense.overrides, [month]: after },
    }),
    following: () => ({
      revisions: [...sortedRevisions(recurringExpense).filter(_ => _.startMonth < month), { startMonth: month, ...after }],
      overrides: pickBy(recurringExpense.overrides, (v, k) => k < month),
    }),
    all: () => ({
      revisions: [{ startMonth: recurringExpense.startMonth, ...after }],
      overrides: {},
    }),
  })[scope]();
};

const isWholeDeletion = (recurringExpense, { scope, month, }) => scope === 'all' || (scope === 'following' && month <= recurringExpense.startMonth);

const applyDelete = (recurringExpense, { scope, month, }) => ({
  single: () => ({
    overrides: { ...recurringExpense.overrides, [month]: { deleted: true } },
  }),
  following: () => ({
    endMonth: shiftMonth(month, -1),
    revisions: sortedRevisions(recurringExpense).filter(_ => _.startMonth < month),
    overrides: pickBy(recurringExpense.overrides, (v, k) => k < month),
  }),
})[scope]();

const changedFieldNames = (before = {}, after = {}) => valueFieldNames.filter(_ => !isEqual(before[_], after[_]));

const timeline = (recurringExpense) => {
  const revisions = sortedRevisions(recurringExpense);
  const revisionEntries = revisions.map((revision, index) => ({
    month: revision.startMonth,
    kind: index === 0 ? 'start' : 'from',
    before: index === 0 ? null : pick(revisions[index - 1], valueFieldNames),
    after: pick(revision, valueFieldNames),
  }));
  const overrideEntries = Object.entries(recurringExpense.overrides || {})
    .filter(([month]) => isActiveAt(recurringExpense, month))
    .map(([month, override]) => ({
      month,
      kind: override.deleted ? 'skip' : 'only',
      before: pick(revisionAt(recurringExpense, month), valueFieldNames),
      after: override.deleted ? null : pick(override, valueFieldNames),
    }));
  const endEntries = recurringExpense.endMonth != null ? [{ month: recurringExpense.endMonth, kind: 'end' }] : [];
  return sortBy([...revisionEntries, ...overrideEntries, ...endEntries], ['month', _ => ['start', 'from', 'only', 'skip', 'end'].indexOf(_.kind)]);
};

exports.valueFieldNames = valueFieldNames;
exports.scopes = scopes;
exports.shiftMonth = shiftMonth;
exports.resolve = resolve;
exports.resolveAll = resolveAll;
exports.newRecurringExpense = newRecurringExpense;
exports.applyEdit = applyEdit;
exports.isWholeDeletion = isWholeDeletion;
exports.applyDelete = applyDelete;
exports.changedFieldNames = changedFieldNames;
exports.timeline = timeline;
